<template>
  <div>
    <div v-if="loading" class="pa-4">
      <v-skeleton-loader type="card" class="mb-4" />
      <v-skeleton-loader type="list-item@4" class="mb-4" />
      <v-skeleton-loader type="card" />
    </div>

    <template v-else>
      <v-card outlined class="pa-4 mb-4">
        <div class="recon-row">
          <span>{{ $t("finance_opening_balance") }}</span>
          <strong>{{ formatCurrency(openingBalance) }}</strong>
        </div>
        <div v-for="line in reconciliationLines" :key="line.value" class="recon-row">
          <span class="d-flex align-center">
            <span class="mf-dot mr-2" :style="{ background: line.color }" />
            {{ $t(line.labelKey) }}
          </span>
          <strong :style="{ color: line.amount >= 0 ? '#00b37e' : '#d9534f' }">
            {{ line.amount >= 0 ? "+" : "" }}{{ formatCurrency(line.amount) }}
          </strong>
        </div>

        <v-divider class="my-3" />

        <div class="recon-row total">
          <span>{{ $t("finance_current_balance") }}</span>
          <strong>{{ formatCurrency(closingBalance) }}</strong>
        </div>
      </v-card>

      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 d-block">
          <div class="d-flex flex-wrap align-center" style="gap: 12px">
            <span>{{ $t("finance_table_label") }}</span>
            <v-spacer />
            <span
              v-for="meta in moneyFlowLegend"
              :key="meta.value"
              class="d-flex align-center text-caption grey--text text--darken-2"
            >
              <span class="mf-dot mr-1" :style="{ background: meta.color }" />
              {{ meta.label }}
            </span>
          </div>
          <div class="d-flex flex-wrap align-center mt-3" style="gap: 12px">
            <text-field
              v-model="search"
              :label="$t('finance_search_transactions')"
              kind="search"
              clearable
              hide-details
              style="max-width: 260px"
            />
            <dropdown
              v-model="filterPeriod"
              :items="periodOptions"
              item-text="label"
              item-value="value"
              :label="$t('finance_date')"
              hide-details
              style="max-width: 220px"
            />
            <dropdown
              v-model="filterMoneyFlow"
              :items="moneyFlowOptions"
              item-text="label"
              item-value="value"
              :label="$t('finance_money_flow')"
              clearable
              hide-details
              style="max-width: 220px"
            />
          </div>
        </v-card-title>

        <v-data-table
          :headers="ledgerHeaders"
          :items="visibleEntries"
          mobile-breakpoint="0"
          item-key="id"
          :no-data-text="$t('finance_no_ledger_items_filtered')"
          class="striped-table"
        >
          <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
          <template v-slot:item.description="{ item }">{{ describe(item.description) }}</template>
          <template v-slot:item.moneyFlow="{ item }">
            <span v-if="moneyFlowMeta(item.money_flow)" class="d-flex align-center">
              <span
                class="mf-dot mr-2"
                :style="{ background: moneyFlowMeta(item.money_flow).color }"
              />
              {{ $t(moneyFlowMeta(item.money_flow).labelKey) }}
            </span>
            <span v-else>—</span>
          </template>
          <template v-slot:item.debit="{ item }">
            <span style="color: #d9820f; font-weight: 600">
              {{ item.amount < 0 ? formatCurrency(Math.abs(item.amount)) : "—" }}
            </span>
          </template>
          <template v-slot:item.credit="{ item }">
            <span style="color: #00b37e; font-weight: 600">
              {{ item.amount > 0 ? formatCurrency(item.amount) : "—" }}
            </span>
          </template>
          <template v-slot:item.balance="{ item }">
            <span class="font-weight-bold">{{ formatCurrency(item.balance) }}</span>
          </template>
        </v-data-table>
      </v-card>

      <v-card outlined class="pa-4">
        <p class="section-label">{{ $t("finance_bottom_label") }}</p>
        <v-row>
          <v-col cols="12" sm="4">
            <p class="footer-label">{{ $t("finance_opening_balance") }}</p>
            <p class="footer-value">{{ formatCurrency(openingBalance) }}</p>
          </v-col>
          <v-col cols="12" sm="4">
            <p class="footer-label">{{ $t("finance_changes") }}</p>
            <p class="footer-value" :class="netChange >= 0 ? 'positive' : 'negative'">
              {{ netChange >= 0 ? "+" : "" }}{{ formatCurrency(netChange) }}
            </p>
          </v-col>
          <v-col cols="12" sm="4">
            <p class="footer-label">{{ $t("finance_closing_balance") }}</p>
            <p class="footer-value">{{ formatCurrency(closingBalance) }}</p>
          </v-col>
        </v-row>
      </v-card>
    </template>
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";
import { MONEY_FLOW_META, TRANSACTION_TYPE_LABEL_KEYS, toDateParam } from "./constants";

// Stariji odgovori su slali description kao čist kod ("ORDER_REVENUE") ili
// "KOD - opis" (npr. "CASH_RECEIVED - Kupac platio restoranu pri preuzimanju").
// Backend sada (13.09.2026 izvještaj) šalje čitljiv tekst direktno i posebno
// money_flow polje po redu - kategorizacija/filter se zato oslanja na
// money_flow (pouzdano), a ne na parsiranje description teksta.
function splitDescription(raw) {
  const separatorIndex = raw.indexOf(" - ");
  if (separatorIndex === -1) return { code: raw, detail: null };
  return { code: raw.slice(0, separatorIndex), detail: raw.slice(separatorIndex + 3) };
}

export default {
  name: "FinanceTabBalanceLedger",

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    openingBalance: 0,
    ledgerEntries: [],
    currentBalanceFromApi: null,
    filterPeriod: "all",
    filterMoneyFlow: null,
    search: "",
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    periodOptions() {
      return [
        { label: this.$t("finance_all"), value: "all" },
        { label: this.$t("finance_last_30_days"), value: "30d" },
        { label: this.$t("finance_last_90_days"), value: "90d" },
        { label: this.$t("finance_this_year"), value: "year" },
      ];
    },

    moneyFlowOptions() {
      return Object.keys(MONEY_FLOW_META).map((value) => ({
        value,
        label: this.$t(MONEY_FLOW_META[value].labelKey),
      }));
    },

    moneyFlowLegend() {
      return Object.keys(MONEY_FLOW_META).map((value) => ({
        value,
        color: MONEY_FLOW_META[value].color,
        label: this.$t(MONEY_FLOW_META[value].labelKey),
      }));
    },

    ledgerHeaders() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_description"), value: "description" },
        { text: this.$t("finance_money_flow"), value: "moneyFlow", sortable: false },
        { text: this.$t("finance_debit"), value: "debit", align: "end" },
        { text: this.$t("finance_credit"), value: "credit", align: "end" },
        { text: this.$t("finance_balance"), value: "balance", align: "end" },
      ];
    },

    // Tekući saldo po redu - backend vraća samo konačan current_balance, pa
    // frontend računa running_balance lokalno (opening_balance + kumulativni
    // zbir). Vidi backend izvještaj 13.09.2026 - running na najstarijem/
    // najnovijem redu mora se poklopiti sa current_balance (sanity check niže).
    entriesWithBalance() {
      let balance = this.openingBalance;
      return [...this.ledgerEntries]
        .sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
        .map((entry) => {
          balance += Number(entry.amount) || 0;
          return { ...entry, balance };
        })
        .reverse();
    },

    // Rezime po money_flow (ko kome duguje) - pouzdanije od starog parsiranja
    // description teksta, koje je zavisilo od "KOD - opis" formata koji
    // backend više ne garantuje (vidi napomenu o "Ostalo" u izvještaju).
    reconciliationLines() {
      const totals = {};
      this.ledgerEntries.forEach((e) => {
        const key = e.money_flow;
        if (!key) return;
        totals[key] = (totals[key] || 0) + (Number(e.amount) || 0);
      });

      return Object.keys(totals)
        .filter((key) => MONEY_FLOW_META[key])
        .map((key) => ({
          value: key,
          color: MONEY_FLOW_META[key].color,
          labelKey: MONEY_FLOW_META[key].labelKey,
          amount: totals[key],
        }));
    },

    netChange() {
      return this.ledgerEntries.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    },

    closingBalance() {
      // Backend šalje current_balance direktno - koristimo ga kad postoji,
      // client-side zbir je samo fallback (npr. ako je odgovor bez tog polja).
      return this.currentBalanceFromApi != null
        ? this.currentBalanceFromApi
        : this.openingBalance + this.netChange;
    },

    // Pretraga i money_flow filter su frontend-only (bez API poziva) - po
    // već učitanim redovima, isti obrazac kao na Transakcije tabu. customSearch
    // (main.js) - ista latinica/ćirilica normalizacija kao svuda drugo u appu.
    visibleEntries() {
      const filtered = this.filterMoneyFlow
        ? this.entriesWithBalance.filter((entry) => entry.money_flow === this.filterMoneyFlow)
        : this.entriesWithBalance;
      return this.customSearch(filtered, this.ledgerHeaders, this.search, ["source_id"]);
    },
  },

  watch: {
    filterPeriod() {
      this.load();
    },
  },

  created() {
    this.load();
  },

  methods: {
    formatCurrency(value) {
      return `${Number(value || 0).toFixed(2)} ${this.restaurantCurrency}`;
    },

    formatDate(isoDate) {
      return new Date(isoDate).toLocaleDateString("sr-RS", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    },

    moneyFlowMeta(value) {
      return value ? MONEY_FLOW_META[value] || null : null;
    },

    describe(description) {
      const { code, detail } = splitDescription(description || "");
      // Backend često već šalje čitljiv opis posle " - " (npr. "Kupac platio
      // restoranu pri preuzimanju") - prikaži taj tekst kad postoji.
      if (detail) return detail;
      const key = TRANSACTION_TYPE_LABEL_KEYS[code];
      if (key) return this.$t(key);
      // Backend sada šalje čitljiv opis direktno (bez "KOD - " prefiksa) -
      // prikaži ga kako jeste umjesto da ga tretiramo kao nepoznat kod.
      return description || "";
    },

    // Preset -> {from, to} (backend parametri, već implementirano). "all"
    // šalje oba kao undefined = cijela istorija.
    periodToDateRange() {
      if (this.filterPeriod === "all") return {};
      const now = new Date();
      if (this.filterPeriod === "year") {
        const from = new Date(now.getFullYear(), 0, 1);
        return { from: toDateParam(from), to: toDateParam(now) };
      }
      const days = { "30d": 30, "90d": 90 }[this.filterPeriod];
      const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      return { from: toDateParam(from), to: toDateParam(now) };
    },

    async load() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { from, to } = this.periodToDateRange();
        const { data } = await restaurantFinanceService.getBalanceLedger(
          this.restaurantId,
          { from, to }
        );
        const payload = data?.data || {};
        this.openingBalance = payload.opening_balance || 0;
        this.ledgerEntries = payload.ledger_entries || [];
        this.currentBalanceFromApi =
          payload.current_balance != null ? Number(payload.current_balance) : null;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_balance_ledger_load_error"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.recon-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #6b7685;
  padding: 4px 0;
}

.mf-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
}

.recon-row.total {
  font-size: 1.15rem;
  color: #0b1220;
  font-weight: 700;
}

.recon-row.total strong {
  color: #0b1220;
}

.section-label {
  margin: 0 0 10px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9aa4b2;
}

.footer-label {
  margin: 0 0 4px;
  font-size: 0.75rem;
  color: #9aa4b2;
}

.footer-value {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  color: #0b1220;
}

.footer-value.positive {
  color: #00b37e;
}

.footer-value.negative {
  color: #d9820f;
}
</style>
